import { Alert, Card, Group, List, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCircleCheck, IconCircleDashed, IconInfoCircle } from '@tabler/icons-react';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { BrandAssetBuilderData } from 'src/secret-menu/brand-asset-builder/types';

type ChecklistItem = {
  label: string;
  done: boolean;
};

function ChecklistRow({ label, done }: ChecklistItem) {
  return (
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon variant="light" color={done ? 'green' : 'gray'} size={24} radius="xl">
        {done ? <IconCircleCheck size={16} /> : <IconCircleDashed size={16} />}
      </ThemeIcon>
      <Text size="textSm" c={done ? 'gray.8' : 'gray.6'}>
        {label}
      </Text>
    </Group>
  );
}

export default function GenerateStep(props: StepProps<BrandAssetBuilderData>) {
  const { data } = props;

  const checklist: ChecklistItem[] = [
    { label: 'Tenant selected', done: data.company !== null },
    { label: 'Logo available', done: data.logoUrl !== null },
    { label: 'Palette approved', done: true },
    { label: 'Title page style selected', done: true },
  ];

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Generate branding pack"
      description="Review the checklist below before generating the branding pack for this tenant."
      continueText="Generate"
      showClientName={false}
      hideProgress
      allowVerticalOverflow
      footer={
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
          Generation isn&apos;t available yet — this is coming in a later update to this tool.
        </Alert>
      }
    >
      <Stack gap="xl">
        <Card withBorder radius="md" p="lg">
          <Stack gap="md">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
              Pre-generation checklist
            </Text>
            <Stack gap="sm">
              {checklist.map(item => (
                <ChecklistRow key={item.label} {...item} />
              ))}
            </Stack>
          </Stack>
        </Card>

        <Card withBorder radius="md" p="lg">
          <Stack gap="md">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
              Files to be generated
            </Text>
            <List spacing="xs" size="sm" c="gray.7">
              <List.Item>export_assets.docx — cloned master with patched branding</List.Item>
              <List.Item>title_page.docx — placeholder replacement and image insertion</List.Item>
              <List.Item>company_logo.png — approved tenant logo as PNG</List.Item>
              <List.Item>title_page_image.png/.jpeg — branded background artwork</List.Item>
            </List>
          </Stack>
        </Card>
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
